import { useEffect, useRef, useState } from "react";

import Header from "../components/HeaderV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/NoticeV2.module.css";
import ErrMsg from "../ErrMsg";
import { NoticeType, useNotice, useNoticeMutations } from "../NoticeContext";
import Editor from "../components/WYSIWYG";

type EditorAsset = { tempUrl: string; file: File; type: "img" | "video" };

const NoticeV2 = () => {
    const partnerId = sessionStorage.getItem("partnerId") || "-";
    const { notices } = useNotice();
    const [addNotice, setAddNotice] = useState<boolean>(false);
    const [detailNotice, setDetailNotice] = useState<NoticeType | null>(null);

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>공지사항 관리</h4>
                    <p className={common["text--copy"]}>게시할 공지사항을 등록하고 확인합니다.</p>

                    <section className={style["control-panel"]}>
                        <div className={style["panel-header"]}>
                            <div>
                                <p className={style["section-kicker"]}>게시 대상</p>
                                <p className={style["panel-title"]}>
                                    {partnerId} · 등록된 공지 {notices.length}개
                                </p>
                            </div>
                            <button className={style["button--primary"]} onClick={() => setAddNotice(true)}>
                                공지사항 등록
                            </button>
                        </div>
                    </section>

                    <NoticeTable onOpenDetail={(notice) => setDetailNotice(notice)} />
                </div>
            </div>

            {addNotice && <AddNotice close={() => setAddNotice(false)} onSaved={() => setAddNotice(false)} />}
            {detailNotice && <NoticeDetailPopup notice={detailNotice} close={() => setDetailNotice(null)} />}
        </>
    );
};

const NoticeTable = ({ onOpenDetail }: { onOpenDetail: (notice: NoticeType) => void }) => {
    const { notices } = useNotice();
    const [search, setSearch] = useState<string>("");

    const filteredNotices = notices.filter((notice) => {
        const keyword = search.toLowerCase();
        return search === "" || notice.title.toLowerCase().includes(keyword);
    });

    return (
        <section className={style["notice-panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <p className={style["section-kicker"]}>공지사항 목록</p>
                    <h5 className={style["panel-title"]}>등록된 공지사항</h5>
                    <p className={style["panel-copy"]}>총 {notices.length}개 중 {filteredNotices.length}개 표시</p>
                </div>
            </div>
            <div className={style["toolbar"]}>
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="공지 제목으로 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className={style["wrapper--notice-list"]}>
                <div className={[style["notice-row"], style["notice-row--head"]].join(" ")}>
                    <p>제목</p>
                    <p>작성일</p>
                    <p>관리</p>
                </div>
                {filteredNotices.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                {filteredNotices.map((notice) => (
                    <NoticeRow key={notice.id} notice={notice} onOpen={() => onOpenDetail(notice)} />
                ))}
            </div>
        </section>
    );
};

const NoticeRow = ({ notice, onOpen }: { notice: NoticeType; onOpen: () => void }) => (
    <div className={style["unit--notice-list"]} onClick={onOpen}>
        <div className={style["notice-row"]}>
            <p className={style["notice-title"]}>{notice.title}</p>
            <p className={style["notice-date"]}>{formatDateTime(notice.timestamp)}</p>
            <div className={style["row-actions"]}>
                <button className={style["button--ghost"]}>상세</button>
            </div>
        </div>
    </div>
);

const AddNotice = ({ close, onSaved }: { close: () => void; onSaved?: () => void }) => {
    const { createNotice } = useNoticeMutations();
    const [title, setTitle] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [editorAssets, setEditorAssets] = useState<EditorAsset[]>([]);
    const [active, setActive] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(true);

    useEffect(() => {
        setActive(true);
        return () => setIsMounted(false);
    }, []);

    const hide = () => {
        if ((title.trim() || content.trim()) && !window.confirm("작성 중인 내용을 닫으시겠습니까?")) return;
        setActive(false);
        setTimeout(() => {
            if (isMounted) close();
        }, 240);
    };

    const saveNotice = async () => {
        if (saving || !isMounted) return;
        setSaving(true);
        try {
            await createNotice(title, content, editorAssets);
            if (isMounted) onSaved?.();
        } catch (err: any) {
            console.error(err);
            if (isMounted) alert(err.message || "저장 중 에러가 발생했습니다.");
        } finally {
            if (isMounted) setSaving(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>공지사항 등록</p>
                        <h4 className={style["modal-title"]}>새 공지 작성</h4>
                        <p className={style["modal-copy"]}>현재 파트너사에 게시됩니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>공지 내용</h5>
                            <p>제목과 본문을 입력한 뒤 등록합니다.</p>
                        </div>
                        <Editor
                            initialTitle={title}
                            initialContent={content}
                            onTitleChange={setTitle}
                            onChange={setContent}
                            onAssetsChange={setEditorAssets}
                        />
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={saveNotice} disabled={saving}>
                        {saving ? "저장 중" : "등록"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const NoticeDetailPopup = ({ notice, close }: { notice: NoticeType; close: () => void }) => {
    const { deleteNoticeWithConfirm, sanitizeHtml } = useNoticeMutations();
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => setActive(true), []);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            const result = await deleteNoticeWithConfirm(notice.id);
            if (result && result.success) hide();
        } catch (err: any) {
            console.error(err);
            alert(ErrMsg[err?.message] || err.message || "삭제 중 에러가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>공지사항 상세</p>
                        <h4 className={style["modal-title"]}>{notice.title}</h4>
                        <p className={style["modal-copy"]}>{formatDateTime(notice.timestamp)}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div
                        ref={contentRef}
                        className={style["container--notice"]}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.content) }}
                    />
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--danger"]} onClick={handleDelete} disabled={loading}>
                        {loading ? "삭제 중" : "삭제"}
                    </button>
                    <button className={style["button--secondary"]} onClick={hide}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

const formatDateTime = (timestamp?: number) => (timestamp ? new Date(Number(timestamp)).toLocaleString("ko-KR") : "-");

export default NoticeV2;
